import type { Activity } from '@orus.eu/activity'
import { objectKeys } from '@orus.eu/utils'

/**
 * Restaurants (REST) and retailers and service providers (RETA) are two programmes with their own agreed terms,
 * revenue cap and restaurant-only exclusions.
 */
export const frMrpaxeSegments = ['REST', 'RETA'] as const
export type FrMrpaxeSegment = (typeof frMrpaxeSegments)[number]

export const frMrpaxeSegmentLabels: Record<FrMrpaxeSegment, string> = {
  REST: 'Cafés et Restaurants',
  RETA: 'Artisans Commerçants et Prestataires de service',
}

/**
 * The risk class an activity is underwritten in. Most classes are one activity; the restaurant ones are refined by
 * the activity-specific answers (pizza share, cooking equipment, kebab).
 */
export const frMrpaxeActivityClassLabels = {
  coffeeBar: 'Bar-café (hors bureau de tabac)',
  creperie: 'Crêperie',
  iceCream: 'Glacier',
  teaRoom: 'Salon de thé ou tarterie',
  traditionalRestaurant: 'Restaurant traditionnel (hors pizzeria)',
  pizzeriaWithoutWoodOven: 'Pizzeria (sans four à bois)',
  kebab: 'Kebab',
  fastFoodWithCookingEquipment: 'Restauration rapide (hors kebab) avec matériel de cuisson',
  fastFoodWithoutCookingEquipment: 'Restauration rapide (hors kebab) sans matériel de cuisson',
  sushiWithCookingEquipment: 'Sushi avec matériel de cuisson',
  sushiWithoutCookingEquipment: 'Sushi sans matériel de cuisson autre que pour le riz',
  caterer: 'Traiteur (restauration)',
  delicatessen: 'Épicerie fine',
  generalFoodStore: 'Alimentation générale, Epicerie, Surface de vente inférieure à 120 m²',
  butcher: 'Boucher-charcutier (artisan)',
  baker: 'Boulanger-Pâtissier (artisan)',
  breadShop: 'Vente de pain ou de viennoiseries sans fabrication, avec ou sans terminal de cuisson (détaillant)',
  coffeeAndTeaShop: 'Vente de cafés et thés, avec ou sans torréfaction (détaillant)',
  confectioner: 'Vente de confiserie, chocolats (détaillant) avec ou sans fabrication',
  cheeseShop: 'Crémerie, fromagerie (détaillant)',
  dieteticProductsShop:
    'Vente au détail de produits de diététique fabriqués en Union Européenne et conformes aux normes en vigueur en France',
  greengrocer: 'Vente de fruits, légumes, primeurs (détaillant)',
  fishmonger: 'Poissonnerie, vente de coquillages et crustacés (détaillant)',
  frozenFoodShop: 'Vente au détail de produits surgelés',
  wineShop: 'Vente au détail de vins et spiritueux',
  regionalProductsShop: 'Vente de conserves, huiles et autres produits régionaux (détaillant)',
  diyShop: "Vente, location d'articles de bricolage, outillage (détaillant)",
  giftShop: "Cadeaux et objets de décoration sans vente de meubles d'une valeur unitaire d'achat > 300 € (détaillant)",
  householdAppliancesShop:
    "Vente au détail, location d'appareils électroménagers sans produits audiovisuels, multimédias ni de téléphonie",
  householdLinenShop: 'Vente au détail de linge de maison',
  lightingShop:
    "Vente au détail de luminaires, d'appareils d'éclairage domestiques sans produits audiovisuels, multimédias ni de téléphonie",
  sewingMachinesShop: 'Vente au détail de machines à coudre ou à tricoter',
  hardwareStore: 'Quincaillier, droguiste, sans laboratoire ni préparation de produits chimiques (détaillant)',
  minuteServices: 'Services minute (Clés, Imprimerie, Talons minute, ...)',
  tablewareShop:
    'Vente au détail de vaisselle et arts de la table (cristaux, faïence, porcelaine, verrerie, coutellerie)',
  applianceRepairer: "Dépanneur, réparateur d'électroménager, de produits audiovisuels et multimédias",
  clothingAccessoriesShop:
    "Vente au détail d'accessoires de l'habillement (gants, cravates, ceintures, chapeaux,...) sans bijoux",
  costumeJewelleryShop:
    "Vente au détail de bijoux fantaisie sans objets d'une valeur unitaire d'achat > 300 € sans perçage corporel autre que le pavillon de l'oreille et l'aile du nez par pistolet perce-oreille",
  shoeShop:
    "Vente au détail de chaussures avec chaussures de sport n'excédant pas 20% de la valeur totale des marchandises",
  cobbler: 'Cordonnier sans vente de chaussures, ni de maroquinerie',
  lingerieShop: 'Vente au détail de lingerie',
  leatherGoodsShop: 'Vente au détail de maroquinerie et articles de voyage',
  haberdasheryWithoutClothing: 'Mercerie et vente de laine (sans vente de vêtements et lingerie)',
  haberdasheryWithClothing: 'Mercerie et vente de laine (avec vente de vêtements et lingerie)',
  readyToWearShop:
    'Vente au détail de vêtements prêt-à-porter sans cuirs et peaux excédant 20% de la valeur totale du stock',
  childrenClothingShop: 'Vente au détail de vêtements enfants, layette',
  hairdresser: 'Coiffeur (exercice en salon)',
  beautySalon: 'Institut de beauté',
  childcareProductsShop: "Vente au détail d'articles de puériculture",
  nailTechnician: "Prothésiste et styliste d'ongles (en salon)",
  florist: 'Fleuriste (vente au détail, location de fleurs et plantes)',
  musicalInstrumentsShop: "Vente au détail et réparation d'instruments de musique",
  toyShop: 'Vente au détail de jeux, jouets et modèles réduits',
  photoLaboratory: 'Laboratoire de développement et de tirage photo',
  bookshop: 'Librairie, papeterie (détaillant) presse sans activité de tabac',
  petGroomer: "Toilettage d'animaux (salon de)",
  artSuppliesShop: 'Vente au détail de matériels pour loisirs créatifs (peinture, chevalets,...)',
  photographerWithoutEquipmentSale: "Salon de photographie et vidéo sans vente d'appareils ni fournitures",
  photographerWithEquipmentSale: "Salon de photographie et vidéo avec vente d'appareils et de fournitures",
  cardShop: 'Carterie, vente de gadgets (détaillant)',
  souvenirShop: 'Vente de souvenirs',
  funeraryArticlesShop: "Vente au détail d'articles funéraires sans activité de pompes funèbres",
  officeSuppliesShop: "Vente au détail d'articles de bureau sans vente de matériel bureautique / informatique",
  computerEquipmentShop: 'Vente de matériel bureautique et informatique (détaillant) sans conception de logiciels',
  furnitureAndBeddingShop: 'Meuble et literie',
  optician: 'Artisan lunetier - Opticien',
  secondHandBooksShop: "Vente de livres et de disques d'occasions",
  pharmacy: 'Pharmacie',
  selfServiceLaundry: 'Laverie automatique',
  petSuppliesShop: "Vente d'accessoires et alimentation pour animaux",
  minimarket: 'Supérette (de 120m² à 400m²)',
  secondHandClothingShop: "Vêtements et chaussures d'occasions (Friperie)",
  clothingAlterations: 'Retoucherie',
  kitchenwareShop: "Vente d'ustensiles pour cuisines (détaillant)",
  selfServiceCopyShop: 'Copie-minute en libre service',
} as const satisfies Record<string, string>

export type FrMrpaxeActivityClass = keyof typeof frMrpaxeActivityClassLabels
export const frMrpaxeActivityClasses = objectKeys(frMrpaxeActivityClassLabels)

/** The activity-specific questions the classification reads. */
export const frMrpaxeActivityQuestionIds = [
  'hasMoreThanHalfRevenuePizza',
  'hasCookingEquipment',
  'hasKebab',
  'hasCookingEquipmentExceptForRice',
] as const
export type FrMrpaxeActivityQuestionId = (typeof frMrpaxeActivityQuestionIds)[number]

export type FrMrpaxeActivityAnswers = Readonly<Partial<Record<FrMrpaxeActivityQuestionId, boolean | null>>>

type FrMrpaxeActivityClassifier = FrMrpaxeActivityClass | ((answers: FrMrpaxeActivityAnswers) => FrMrpaxeActivityClass)

type FrMrpaxeActivityMappingEntry = { segment: FrMrpaxeSegment; activityClass: FrMrpaxeActivityClassifier }

function pizzeriaIfMostlyPizza(otherwise: FrMrpaxeActivityClass): FrMrpaxeActivityClassifier {
  return (answers) => (answers.hasMoreThanHalfRevenuePizza ? 'pizzeriaWithoutWoodOven' : otherwise)
}

const FR_MRPAXE_PHARMACY_ACTIVITY = '4e9f39c2-c9d7-46c4-a41f-6bec186c75aa'

/**
 * The activities fr-mrpaxe insures. An activity missing from this table is not insurable.
 */
const frMrpaxeActivityMapping: Record<string, FrMrpaxeActivityMappingEntry | undefined> = {
  coffeeBar: { segment: 'REST', activityClass: pizzeriaIfMostlyPizza('coffeeBar') },
  creperie: { segment: 'REST', activityClass: 'creperie' },
  iceCream: { segment: 'REST', activityClass: 'iceCream' },
  teaRoom: { segment: 'REST', activityClass: 'teaRoom' },
  traditionalRestaurant: { segment: 'REST', activityClass: pizzeriaIfMostlyPizza('traditionalRestaurant') },
  pizzeria: { segment: 'REST', activityClass: 'pizzeriaWithoutWoodOven' },
  kebab: { segment: 'REST', activityClass: 'kebab' },
  fastFood: {
    segment: 'REST',
    activityClass: (answers) => {
      if (answers.hasMoreThanHalfRevenuePizza) return 'pizzeriaWithoutWoodOven'
      if (!answers.hasCookingEquipment) return 'fastFoodWithoutCookingEquipment'
      return answers.hasKebab ? 'kebab' : 'fastFoodWithCookingEquipment'
    },
  },
  sushi: {
    segment: 'REST',
    activityClass: (answers) =>
      answers.hasCookingEquipmentExceptForRice ? 'sushiWithCookingEquipment' : 'sushiWithoutCookingEquipment',
  },
  caterer: { segment: 'REST', activityClass: pizzeriaIfMostlyPizza('caterer') },
  delicatessen: { segment: 'REST', activityClass: 'delicatessen' },
  '8a57291b-5faf-43f3-8953-981af539f8aa': { segment: 'RETA', activityClass: 'generalFoodStore' },
  '3f27aba9-187b-4975-b35b-c0925e09ab9f': { segment: 'RETA', activityClass: 'butcher' },
  '854da267-af97-46f4-a2ed-cb829a7def4d': { segment: 'RETA', activityClass: 'baker' },
  'd15dd219-f0ac-4619-b322-bb872d49ae99': { segment: 'RETA', activityClass: 'breadShop' },
  '71db9e3f-35ec-490d-a134-f56aa0cca710': { segment: 'RETA', activityClass: 'coffeeAndTeaShop' },
  '7ac250c9-2c04-4df5-94e0-a17086be02a1': { segment: 'RETA', activityClass: 'confectioner' },
  '93fa60cf-f6c4-499a-b855-bc58b036b573': { segment: 'RETA', activityClass: 'cheeseShop' },
  'ced37235-bc0d-4a81-bdfd-4bc99f2576b8': { segment: 'RETA', activityClass: 'dieteticProductsShop' },
  '1bf42674-0977-4d82-8218-feac24bc9862': { segment: 'RETA', activityClass: 'greengrocer' },
  '1dac3538-9827-4303-bde4-b1d895a83c35': { segment: 'RETA', activityClass: 'fishmonger' },
  '93dcbb77-bd68-4605-8413-dc2990a4c8a8': { segment: 'RETA', activityClass: 'frozenFoodShop' },
  'c5d89007-1585-4bff-8e82-cbc0e713f19b': { segment: 'RETA', activityClass: 'wineShop' },
  '07a5e870-78ad-45e2-b65e-fad0de2a7c40': { segment: 'RETA', activityClass: 'regionalProductsShop' },
  'a0bfd71e-083c-42f1-8b6f-519fc770e0c5': { segment: 'RETA', activityClass: 'diyShop' },
  'bdd5c5ab-1a2a-421f-af9a-a97430b14b95': { segment: 'RETA', activityClass: 'giftShop' },
  'ae7076f5-60a9-46dd-ab2c-9d624ca98fb5': { segment: 'RETA', activityClass: 'householdAppliancesShop' },
  '83ca94fd-568e-4a75-90dc-7aede3bfe7c7': { segment: 'RETA', activityClass: 'householdLinenShop' },
  'b5aa18b7-93e4-4fbf-820c-001463e161a4': { segment: 'RETA', activityClass: 'lightingShop' },
  'a74c6e08-8124-43de-a7e6-762707a97825': { segment: 'RETA', activityClass: 'sewingMachinesShop' },
  '8f6cd5da-bc9a-4240-b4db-519aaaba4f2a': { segment: 'RETA', activityClass: 'hardwareStore' },
  'cac9e6f4-aeb1-4005-956e-c6c66b4f9c0e': { segment: 'RETA', activityClass: 'minuteServices' },
  '99d56449-c6c0-42fe-9cc4-937d41e57525': { segment: 'RETA', activityClass: 'tablewareShop' },
  '30c44302-6362-475a-baae-9f4abe528ebd': { segment: 'RETA', activityClass: 'applianceRepairer' },
  'a1f46975-2c24-4123-8a15-a470ec54d84d': { segment: 'RETA', activityClass: 'clothingAccessoriesShop' },
  '6a58fe26-a769-4d9c-ade9-c4dfb9de37f5': { segment: 'RETA', activityClass: 'costumeJewelleryShop' },
  'a05eaa4e-9c78-474b-878b-6c0c64dd6a80': { segment: 'RETA', activityClass: 'shoeShop' },
  '25600391-c705-4034-a79e-b58b5f9a9ce7': { segment: 'RETA', activityClass: 'cobbler' },
  '3cb47245-519c-4b77-8843-35aae8c18a7b': { segment: 'RETA', activityClass: 'lingerieShop' },
  'd391d169-8868-45ad-a451-8cfb9a4a5967': { segment: 'RETA', activityClass: 'leatherGoodsShop' },
  '54c62891-a276-4051-8723-1f254624a856': { segment: 'RETA', activityClass: 'haberdasheryWithoutClothing' },
  'b6545179-344f-428f-897c-e9ad2f476eec': { segment: 'RETA', activityClass: 'haberdasheryWithClothing' },
  '0c9a9824-1def-49e4-81b0-f1c0100fafd7': { segment: 'RETA', activityClass: 'readyToWearShop' },
  '336c569a-ef6e-4a50-b25a-71d76f1a41db': { segment: 'RETA', activityClass: 'childrenClothingShop' },
  '4f7319d3-1d7f-4f82-86ef-c424c7d386fe': { segment: 'RETA', activityClass: 'hairdresser' },
  '998f6145-741b-464b-9a7a-f3986f5c3402': { segment: 'RETA', activityClass: 'beautySalon' },
  'afc4279f-536e-466c-97d2-4f376485be10': { segment: 'RETA', activityClass: 'childcareProductsShop' },
  'ec08ca50-e20a-4c57-8717-70753baa414b': { segment: 'RETA', activityClass: 'nailTechnician' },
  '628aafd6-e74f-41fe-8047-083caa9c2f59': { segment: 'RETA', activityClass: 'florist' },
  'a53a68c4-0788-4ae0-9ecc-c06994f38bd0': { segment: 'RETA', activityClass: 'musicalInstrumentsShop' },
  '5b181507-f061-4edb-a87d-9f0516ecf2be': { segment: 'RETA', activityClass: 'toyShop' },
  '8d37baf8-70fb-49c5-aa8b-a7271723129c': { segment: 'RETA', activityClass: 'photoLaboratory' },
  '77dcf20b-5213-4da0-892d-c35d6cf44bea': { segment: 'RETA', activityClass: 'bookshop' },
  'a4c7baa6-332c-4647-8e71-3a1270c238c2': { segment: 'RETA', activityClass: 'petGroomer' },
  '9567632f-dc48-4edd-a78e-2897495ccaf0': { segment: 'RETA', activityClass: 'artSuppliesShop' },
  'f68a3981-9ff6-4973-a89b-a5cd0c2cebd0': { segment: 'RETA', activityClass: 'photographerWithoutEquipmentSale' },
  '2eb95c60-a5bc-4dbe-a875-fb973345ae8a': { segment: 'RETA', activityClass: 'photographerWithEquipmentSale' },
  '6687c8b4-df16-428f-bee5-a4a38011a3a5': { segment: 'RETA', activityClass: 'cardShop' },
  '2d87a555-c12f-4727-8055-c8907d070257': { segment: 'RETA', activityClass: 'souvenirShop' },
  'f6aa7928-8f3a-441a-96c2-f7882b937382': { segment: 'RETA', activityClass: 'funeraryArticlesShop' },
  '26b8b554-6caa-4d72-96a3-d143055dcaad': { segment: 'RETA', activityClass: 'officeSuppliesShop' },
  '783fe15a-e3be-41ff-a3b0-fc2bc3b9b5b3': { segment: 'RETA', activityClass: 'computerEquipmentShop' },
  'bcbc1001-667a-46b5-8784-bdaf420b817c': { segment: 'RETA', activityClass: 'furnitureAndBeddingShop' },
  '5693295e-9178-4888-a45b-85f28463a513': { segment: 'RETA', activityClass: 'optician' },
  '12d5859c-4500-4040-b752-0f1a60838acc': { segment: 'RETA', activityClass: 'secondHandBooksShop' },
  [FR_MRPAXE_PHARMACY_ACTIVITY]: { segment: 'RETA', activityClass: 'pharmacy' },
  'f8118c58-465c-4251-81c7-6ed101d2cbfd': { segment: 'RETA', activityClass: 'selfServiceLaundry' },
  'c9736af7-ebd1-4bd9-98cf-692866b3864f': { segment: 'RETA', activityClass: 'petSuppliesShop' },
  '106273a9-d868-4dfc-b426-7474fde515fe': { segment: 'RETA', activityClass: 'minimarket' },
  'a8f6e33a-5316-4903-a6f2-d7bb17028bee': { segment: 'RETA', activityClass: 'secondHandClothingShop' },
  '8181eea6-6f0b-4825-b675-f0ec9c2121c0': { segment: 'RETA', activityClass: 'clothingAlterations' },
  '8d2d4320-d61e-48eb-a65f-027543f916cc': { segment: 'RETA', activityClass: 'kitchenwareShop' },
  'c109080d-80b6-4f2f-9120-ce7d620f8944': { segment: 'RETA', activityClass: 'selfServiceCopyShop' },
}

export function isFrMrpaxeActivity(activity: Activity): boolean {
  return !!frMrpaxeActivityMapping[activity]
}

export function getFrMrpaxeSegment(activity: Activity): FrMrpaxeSegment | undefined {
  return frMrpaxeActivityMapping[activity]?.segment
}

export function getFrMrpaxeActivityClass(
  activity: Activity,
  answers: FrMrpaxeActivityAnswers,
): FrMrpaxeActivityClass | undefined {
  const entry = frMrpaxeActivityMapping[activity]
  if (!entry) return undefined
  return typeof entry.activityClass === 'string' ? entry.activityClass : entry.activityClass(answers)
}

export function isFrMrpaxePharmacy(activity: Activity): boolean {
  return activity === FR_MRPAXE_PHARMACY_ACTIVITY
}

const REQUALIFIABLE_AS_PIZZERIA_ACTIVITIES: readonly string[] = [
  'traditionalRestaurant',
  'coffeeBar',
  'caterer',
  'fastFood',
  'kebab',
]

/**
 * `fromRequalification` tells a pizzeria from a restaurant underwritten as one because pizza makes more than half of
 * its revenue: the two have their own exclusion wording.
 */
export type FrMrpaxePizzeriaStatus = { isPizzeria: false } | { isPizzeria: true; fromRequalification: boolean }

export function getFrMrpaxePizzeriaStatus(
  activity: Activity,
  answers: FrMrpaxeActivityAnswers,
): FrMrpaxePizzeriaStatus {
  if (activity === 'pizzeria') return { isPizzeria: true, fromRequalification: false }
  if (REQUALIFIABLE_AS_PIZZERIA_ACTIVITIES.includes(activity) && answers.hasMoreThanHalfRevenuePizza) {
    return { isPizzeria: true, fromRequalification: true }
  }
  return { isPizzeria: false }
}

/**
 * The retailers whose perishable goods kept under controlled temperature are covered, on top of every restaurant. The
 * tariff prices the cover whenever it applies, the minimarket included, although Wakam did not price it there.
 */
const PRESERVED_GOODS_RETAIL_ACTIVITIES: readonly string[] = [
  '8a57291b-5faf-43f3-8953-981af539f8aa',
  '3f27aba9-187b-4975-b35b-c0925e09ab9f',
  '854da267-af97-46f4-a2ed-cb829a7def4d',
  '93fa60cf-f6c4-499a-b855-bc58b036b573',
  '628aafd6-e74f-41fe-8047-083caa9c2f59',
  '1dac3538-9827-4303-bde4-b1d895a83c35',
  '106273a9-d868-4dfc-b426-7474fde515fe',
  'ced37235-bc0d-4a81-bdfd-4bc99f2576b8',
  '93dcbb77-bd68-4605-8413-dc2990a4c8a8',
  'c5d89007-1585-4bff-8e82-cbc0e713f19b',
  '71db9e3f-35ec-490d-a134-f56aa0cca710',
  '7ac250c9-2c04-4df5-94e0-a17086be02a1',
  '07a5e870-78ad-45e2-b65e-fad0de2a7c40',
  '1bf42674-0977-4d82-8218-feac24bc9862',
  'd15dd219-f0ac-4619-b322-bb872d49ae99',
]

/**
 * Every restaurant has the preserved goods cover: the agreed terms print it and the tariff prices it. Among retailers,
 * only the food retailers have it.
 */
export function hasFrMrpaxePreservedGoodsCover(activity: Activity): boolean {
  return getFrMrpaxeSegment(activity) === 'REST' || PRESERVED_GOODS_RETAIL_ACTIVITIES.includes(activity)
}

/** Pharmacies are not covered for professional liability. */
export function hasFrMrpaxePublicLiabilityCover(activity: Activity): boolean {
  return !isFrMrpaxePharmacy(activity)
}
